<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  addCompetence,
  addDnaScreen,
  addMedical,
  addWorkerDocument,
  CARD_TYPE_SUGGESTIONS,
  assignProject,
  deactivateWorker,
  endAssignment,
  extractCompetenceCard,
  getDownloadUrl,
  getWorker,
  listCompetencyRoles,
  listProjects,
  reactivateWorker,
  removeCompetence,
  geocodePostcode,
  getAccommodations,
  removeDnaScreen,
  removeMedical,
  removeWorkerAddress,
  removeWorkerDocument,
  saveWorkerAddress,
  updateWorker,
  uploadFile,
  verifyCompetence,
  type RosterFinding,
  type WorkerAddress,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";

const LAYER_LABEL: Record<string, string> = {
  minimum_core: "Minimum-core",
  sentinel: "Sentinel",
  prerequisites: "Pre-req",
};

function findingDetail(f: RosterFinding): string {
  switch (f.reason) {
    case "missing":
      return "missing";
    case "below_level":
      return `below level (need ${f.required}, has ${f.held ?? "?"})`;
    case "expired":
      return "expired";
    case "expiring":
      return `expiring${f.days_left != null ? ` in ${f.days_left}d` : ""}`;
    case "level_unknown":
      return `level not recorded (need ${f.required})`;
    case "not_tracked":
      return "not tracked yet — needs a data source";
    default:
      return f.reason;
  }
}

const route = useRoute();
const id = computed(() => route.params.id as string);
const queryClient = useQueryClient();

const { data, isPending, isError, error } = useQuery({
  queryKey: ["worker", id],
  queryFn: () => getWorker(id.value),
});
const { data: projects } = useQuery({
  queryKey: ["projects"],
  queryFn: () => listProjects(),
});
const { data: competencyRoles } = useQuery({
  queryKey: ["competency-roles"],
  queryFn: () => listCompetencyRoles(),
});

const PAYMENT_TYPES = [
  "Day rate", "Per meter dug", "Hourly", "Umbrella", "CIS", "PAYE", "Salaried",
];

async function refresh(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ["worker", id] });
  await queryClient.invalidateQueries({ queryKey: ["workers"] });
}

// ---- Profile image ----
const { data: imageUrl } = useQuery({
  queryKey: ["worker-image", computed(() => data.value?.image_file_id)],
  queryFn: () => getDownloadUrl(data.value!.image_file_id!),
  enabled: () => !!data.value?.image_file_id,
});
const imageBusy = ref(false);
async function onImage(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  imageBusy.value = true;
  try {
    const fileId = await uploadFile(file, "worker_image");
    await updateWorker(id.value, { image_file_id: fileId });
    await refresh();
  } finally {
    imageBusy.value = false;
  }
}
const initials = computed(() =>
  (data.value?.name ?? "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase(),
);

// ---- Details + emergency contact ----
const form = reactive({
  name: "", ref: "", employment_type: "", primary_role: "",
  started_on: "", email: "", phone: "", payment_type: "", utr: "",
});
const ec = reactive({ name: "", relationship: "", phone: "", address: "" });

watch(
  data,
  (w) => {
    if (!w) return;
    form.name = w.name ?? "";
    form.ref = w.ref ?? "";
    form.employment_type = w.employment_type ?? "";
    form.primary_role = w.primary_role ?? "";
    form.started_on = w.started_on ?? "";
    form.email = w.email ?? "";
    form.phone = w.phone ?? "";
    form.payment_type = w.payment_type ?? "";
    form.utr = w.utr ?? "";
    ec.name = w.emergency_contact?.name ?? "";
    ec.relationship = w.emergency_contact?.relationship ?? "";
    ec.phone = w.emergency_contact?.phone ?? "";
    ec.address = w.emergency_contact?.address ?? "";
  },
  { immediate: true },
);

const saving = ref(false);
const saved = ref(false);
const saveError = ref<string | null>(null);

async function saveDetails(): Promise<void> {
  if (!form.name.trim()) return;
  saving.value = true;
  saved.value = false;
  saveError.value = null;
  try {
    const hasEc = ec.name || ec.relationship || ec.phone || ec.address;
    await updateWorker(id.value, {
      name: form.name.trim(),
      ref: form.ref.trim(),
      employment_type: form.employment_type.trim(),
      primary_role: form.primary_role.trim(),
      started_on: form.started_on || null,
      email: form.email.trim(),
      phone: form.phone.trim(),
      payment_type: form.payment_type.trim(),
      utr: form.utr.trim() || null,
      emergency_contact: hasEc
        ? {
            name: ec.name.trim(), relationship: ec.relationship.trim(),
            phone: ec.phone.trim(), address: ec.address.trim(),
          }
        : null,
    });
    await refresh();
    saved.value = true;
  } catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e);
  } finally {
    saving.value = false;
  }
}

async function toggleStatus(e: Event): Promise<void> {
  const active = (e.target as HTMLSelectElement).value === "active";
  if (active === data.value?.active) return;
  await (active ? reactivateWorker(id.value) : deactivateWorker(id.value));
  await refresh();
}

// ---- Project assignments ----
const today = () => new Date().toISOString().slice(0, 10);
const activeAssignments = computed(() =>
  (data.value?.project_assignments ?? []).filter((a) => !a.ended_on),
);
const pastAssignments = computed(() =>
  (data.value?.project_assignments ?? []).filter((a) => a.ended_on),
);
const newAssign = reactive({ project_id: "", role: "", started_on: today() });
const showAssign = ref(false);
const assignBusy = ref(false);
const selectedRoleRequired = computed(
  () => competencyRoles.value?.find((r) => r.role === newAssign.role)?.required ?? [],
);
async function saveAssignment(): Promise<void> {
  if (!newAssign.project_id) return;
  assignBusy.value = true;
  try {
    const proj = projects.value?.find((p) => p.id === newAssign.project_id);
    await assignProject(id.value, {
      project_id: newAssign.project_id,
      project_name: proj?.name,
      role: newAssign.role.trim() || undefined,
      started_on: newAssign.started_on || undefined,
    });
    await refresh();
    newAssign.project_id = newAssign.role = "";
    newAssign.started_on = today();
    showAssign.value = false;
  } finally {
    assignBusy.value = false;
  }
}
async function endAssign(assignmentId: string): Promise<void> {
  await endAssignment(id.value, assignmentId, today());
  await refresh();
}

// ---- D&A screens ----
const newDna = reactive({ screen_type: "", provider: "", date: "", result: "" });
const showDna = ref(false);
const dnaBusy = ref(false);
async function saveDna(): Promise<void> {
  if (!newDna.screen_type.trim()) return;
  dnaBusy.value = true;
  try {
    await addDnaScreen(id.value, {
      screen_type: newDna.screen_type.trim(),
      provider: newDna.provider.trim() || null,
      date: newDna.date || null,
      result: newDna.result.trim() || null,
    });
    await refresh();
    newDna.screen_type = newDna.provider = newDna.date = newDna.result = "";
    showDna.value = false;
  } finally {
    dnaBusy.value = false;
  }
}
async function dropDna(dnaId: string): Promise<void> {
  await removeDnaScreen(id.value, dnaId);
  await refresh();
}

// ---- Documents ----
const docBusy = ref(false);
async function onDocument(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  docBusy.value = true;
  try {
    const fileId = await uploadFile(file, "worker_document");
    await addWorkerDocument(id.value, {
      file_id: fileId, name: file.name,
      mime_type: file.type || null, size_bytes: file.size,
    });
    await refresh();
  } finally {
    docBusy.value = false;
    (e.target as HTMLInputElement).value = "";
  }
}
async function openDocument(fileId: string): Promise<void> {
  window.open(await getDownloadUrl(fileId), "_blank");
}
async function viewCard(c: { front_file_id?: string | null; back_file_id?: string | null }): Promise<void> {
  if (c.front_file_id) window.open(await getDownloadUrl(c.front_file_id), "_blank");
  if (c.back_file_id) window.open(await getDownloadUrl(c.back_file_id), "_blank");
}
async function dropDocument(documentId: string): Promise<void> {
  await removeWorkerDocument(id.value, documentId);
  await refresh();
}

// ---- Competences ----
const LEVEL_LABEL: Record<string, string> = {
  A: "Appreciation", W: "Working", P: "Proficient", S: "Specialist",
};
const newComp = reactive({
  category: "",
  sub_category: "",
  reference: "",
  issuing_body: "",
  issued: "",
  expiry: "",
  level: "",
  alias: "",
  front_file_id: "" as string | null,
  back_file_id: "" as string | null,
  pending: false,
});
function resetComp(): void {
  newComp.category = newComp.sub_category = newComp.reference = "";
  newComp.issuing_body = newComp.issued = newComp.expiry = "";
  newComp.level = newComp.alias = "";
  newComp.front_file_id = newComp.back_file_id = null;
  newComp.pending = false;
}
const showComp = ref(false);
const compBusy = ref(false);

// ---- Scan a card → pre-fill the competence form (assisted entry) ----
const scanBusy = ref(false);
const scanMsg = ref<string | null>(null);
async function scanCard(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []).slice(0, 2); // front [+ back]
  if (!files.length) return;
  scanBusy.value = true;
  scanMsg.value = null;
  try {
    const fileIds: string[] = [];
    for (const f of files) {
      const fid = await uploadFile(f, "competence_card");
      fileIds.push(fid);
      // Keep the card image as evidence on the worker's documents.
      await addWorkerDocument(id.value, {
        file_id: fid,
        name: `Competency card (${fileIds.length === 1 ? "front" : "back"})`,
        mime_type: f.type || null,
        size_bytes: f.size,
      });
    }
    const r = await extractCompetenceCard(id.value, {
      front_file_id: fileIds[0],
      back_file_id: fileIds[1],
    });
    // Pre-fill for the office to confirm — never auto-saved.
    if (r.category) newComp.category = r.category;
    if (r.reference) newComp.reference = r.reference;
    if (r.expiry) newComp.expiry = r.expiry;
    // Keep the card images on the competence itself as evidence.
    newComp.front_file_id = fileIds[0] ?? null;
    newComp.back_file_id = fileIds[1] ?? null;
    showComp.value = true;
    const bits = [
      r.card_type ? `type: ${r.card_type}` : "",
      r.holder_name ? `name: ${r.holder_name}` : "",
    ].filter(Boolean).join(", ");
    scanMsg.value = r.model
      ? `Read from the card — please verify${bits ? ` (${bits})` : ""}. The image is saved under Documents.`
      : "Couldn't read the card automatically — enter the details below. The image is saved under Documents.";
    await refresh();
  } finally {
    scanBusy.value = false;
    input.value = "";
  }
}
async function saveCompetence(): Promise<void> {
  if (!newComp.category.trim()) return;
  compBusy.value = true;
  try {
    await addCompetence(id.value, {
      category: newComp.category.trim(),
      sub_category: newComp.sub_category.trim() || null,
      alias: newComp.alias.trim() || null,
      reference: newComp.reference.trim() || null,
      issuing_body: newComp.issuing_body.trim() || null,
      issued: newComp.issued || null,
      expiry: newComp.expiry || null,
      level: newComp.level || null,
      front_file_id: newComp.front_file_id || null,
      back_file_id: newComp.back_file_id || null,
      verification: newComp.pending ? "pending" : "verified",
    });
    await refresh();
    resetComp();
    showComp.value = false;
  } finally {
    compBusy.value = false;
  }
}
async function verifyCard(cid: string): Promise<void> {
  await verifyCompetence(id.value, cid);
  await refresh();
}
async function dropCompetence(cid: string): Promise<void> {
  await removeCompetence(id.value, cid);
  await refresh();
}

// ---- Medical records ----
const newMed = reactive({ level: "", provider: "", examined: "", expires: "", result: "" });
const showMed = ref(false);
const medBusy = ref(false);
async function saveMedical(): Promise<void> {
  if (!newMed.level.trim()) return;
  medBusy.value = true;
  try {
    await addMedical(id.value, {
      level: newMed.level.trim(),
      provider: newMed.provider.trim() || null,
      examined: newMed.examined || null,
      expires: newMed.expires || null,
      result: newMed.result.trim() || null,
    });
    await refresh();
    newMed.level = newMed.provider = newMed.examined = newMed.expires = newMed.result = "";
    showMed.value = false;
  } finally {
    medBusy.value = false;
  }
}
async function dropMedical(mid: string): Promise<void> {
  await removeMedical(id.value, mid);
  await refresh();
}

// ---- Helpers ----
function fmt(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "—";
}
function status(expiry: string | null): { label: string; cls: string } {
  if (!expiry) return { label: "Valid", cls: "bg-semantic-success/10 text-semantic-success" };
  const days = Math.floor((new Date(expiry).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { label: "Expired", cls: "bg-semantic-danger/10 text-semantic-danger" };
  if (days <= 60) return { label: "Expiring soon", cls: "bg-semantic-warning/10 text-semantic-warning" };
  return { label: "Valid", cls: "bg-semantic-success/10 text-semantic-success" };
}
const inputCls =
  "rounded-md border border-neutral-300 px-3 py-2 text-sm";

// ---- Worker addresses (home / hotel / alternate, for travel time) ----
const ADDRESS_TYPES = [
  { value: "home", label: "Home" },
  { value: "hotel", label: "Hotel / lodging" },
  { value: "alternate", label: "Alternate" },
];
function blankAddr(): Omit<WorkerAddress, "id"> & { id?: string } {
  return {
    type: "home", label: "", line1: "", line2: "", city: "", postcode: "",
    latitude: null, longitude: null, is_primary: false, accommodation_id: null,
  };
}
const newAddr = reactive(blankAddr());
const showAddr = ref(false);
// Shared accommodation catalogue, to seed a hotel address.
const { data: catalogue } = useQuery({
  queryKey: ["accommodations"],
  queryFn: () => getAccommodations(),
});
const activeAccommodations = computed(() =>
  (catalogue.value?.accommodations ?? []).filter((a) => a.active),
);
function pickAccommodation(id: string): void {
  const a = activeAccommodations.value.find((x) => x.id === id);
  if (!a) return;
  newAddr.label = a.name;
  newAddr.line1 = a.line1;
  newAddr.line2 = a.line2;
  newAddr.city = a.city;
  newAddr.postcode = a.postcode;
  newAddr.latitude = a.latitude;
  newAddr.longitude = a.longitude;
  newAddr.accommodation_id = a.id;
  geoMsg.value = a.latitude != null ? "Coordinates from the catalogue." : null;
}
const addrBusy = ref(false);
const geoBusy = ref(false);
const geoMsg = ref<string | null>(null);
function resetAddr(): void {
  Object.assign(newAddr, blankAddr());
  geoMsg.value = null;
  showAddr.value = false;
}
function editAddr(a: WorkerAddress): void {
  Object.assign(newAddr, a);
  geoMsg.value = a.latitude != null ? `Coordinates on file (${a.latitude.toFixed(2)}, ${a.longitude?.toFixed(2)})` : null;
  showAddr.value = true;
}
async function geocodeAddr(): Promise<void> {
  if (!newAddr.postcode.trim()) return;
  geoBusy.value = true;
  geoMsg.value = null;
  try {
    const r = await geocodePostcode(newAddr.postcode.trim());
    if (r.latitude != null && r.longitude != null) {
      newAddr.latitude = r.latitude;
      newAddr.longitude = r.longitude;
      geoMsg.value = `Coordinates set (${r.latitude.toFixed(2)}, ${r.longitude.toFixed(2)})${r.formatted ? ` — ${r.formatted}` : ""}`;
    } else {
      geoMsg.value = "Couldn't resolve that postcode — check it and retry.";
    }
  } finally {
    geoBusy.value = false;
  }
}
async function saveAddr(): Promise<void> {
  if (!newAddr.postcode.trim()) return;
  addrBusy.value = true;
  try {
    // Geocode on save if not already resolved.
    if (newAddr.latitude == null) await geocodeAddr();
    await saveWorkerAddress(id.value, { ...newAddr });
    await refresh();
    resetAddr();
  } finally {
    addrBusy.value = false;
  }
}
async function dropAddr(addressId: string): Promise<void> {
  await removeWorkerAddress(id.value, addressId);
  await refresh();
}

// Flat compliance dates — kept current automatically from verified cards
// (Sentinel/NCCA/medical), or set on import. A null date reads as "Not on file"
// rather than the green "Valid" that status() gives an open-ended card.
const complianceDates = computed(() => [
  { label: "Sentinel", date: data.value?.sentinel_expiry ?? null, expiry: true },
  { label: "NCCA", date: data.value?.ncca_expiry ?? null, expiry: true },
  { label: "Medical", date: data.value?.medical_expiry ?? null, expiry: true },
  { label: "OH record", date: data.value?.oh_record_date ?? null, expiry: false },
]);
function dateStatus(d: string | null, expiry: boolean): { label: string; cls: string } {
  if (!d) return { label: "Not on file", cls: "bg-neutral-100 text-neutral-400" };
  return expiry
    ? status(d)
    : { label: "On file", cls: "bg-semantic-success/10 text-semantic-success" };
}
</script>

<template>
  <PageLayout>
    <RouterLink
      to="/workers"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Workers
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid max-w-4xl gap-6"
    >
      <!-- Header with image -->
      <header class="flex items-center gap-4">
        <div class="relative">
          <img
            v-if="imageUrl"
            :src="imageUrl"
            alt=""
            class="h-16 w-16 rounded-full object-cover"
          >
          <div
            v-else
            class="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-200 text-lg font-semibold text-neutral-500"
          >
            {{ initials }}
          </div>
          <label
            class="absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-neutral-0 p-1 shadow ring-1 ring-neutral-200"
            :title="imageBusy ? 'Uploading…' : 'Change photo'"
          >
            <svg
              class="h-3.5 w-3.5 text-neutral-500"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 5v14M5 12h14"
              />
            </svg>
            <input
              type="file"
              accept="image/*"
              class="hidden"
              :disabled="imageBusy"
              @change="onImage"
            >
          </label>
        </div>
        <div>
          <p
            v-if="data.ref"
            class="text-sm font-medium text-neutral-400"
          >
            {{ data.ref }}
          </p>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            {{ data.name }}
          </h1>
          <p class="mt-0.5 text-neutral-500">
            {{ data.primary_role || "—" }} · NCCA {{ data.ncca_no }}
            <span :class="data.active ? 'text-semantic-success' : 'text-neutral-400'">
              · {{ data.active ? "Active" : "Inactive" }}</span>
          </p>
        </div>
      </header>

      <!-- Details -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          Worker details
        </h2>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Name</span>
            <input
              v-model="form.name"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Reference</span>
            <input
              v-model="form.ref"
              placeholder="e.g. W-001"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Status</span>
            <select
              :value="data.active ? 'active' : 'inactive'"
              :class="inputCls"
              @change="toggleStatus"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Employment type</span>
            <input
              v-model="form.employment_type"
              placeholder="e.g. Direct employee"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Primary role</span>
            <input
              v-model="form.primary_role"
              placeholder="e.g. HV Jointer"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Payment type</span>
            <select
              v-model="form.payment_type"
              :class="inputCls"
            >
              <option value="">
                —
              </option>
              <option
                v-for="p in PAYMENT_TYPES"
                :key="p"
                :value="p"
              >
                {{ p }}
              </option>
              <option
                v-if="form.payment_type && !PAYMENT_TYPES.includes(form.payment_type)"
                :value="form.payment_type"
              >
                {{ form.payment_type }}
              </option>
            </select></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">UTR
                                              <span class="text-neutral-400">(CIS subcontractors)</span></span>
            <input
              v-model="form.utr"
              placeholder="10-digit UTR"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Started on</span>
            <input
              v-model="form.started_on"
              type="date"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Email</span>
            <input
              v-model="form.email"
              type="email"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Phone</span>
            <input
              v-model="form.phone"
              :class="inputCls"
            ></label>
        </div>

        <h3 class="mb-3 mt-6 text-sm font-semibold text-neutral-700">
          Emergency contact
        </h3>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Name</span>
            <input
              v-model="ec.name"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Relationship</span>
            <input
              v-model="ec.relationship"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Phone</span>
            <input
              v-model="ec.phone"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-sm"><span class="text-neutral-500">Address</span>
            <input
              v-model="ec.address"
              :class="inputCls"
            ></label>
        </div>

        <div class="mt-4 flex items-center gap-3">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="saving || !form.name.trim()"
            @click="saveDetails"
          >
            {{ saving ? "Saving…" : "Save details" }}
          </button>
          <span
            v-if="saved"
            class="text-sm text-semantic-success"
          >Saved.</span>
          <span
            v-if="saveError"
            class="text-sm text-semantic-danger"
          >{{ saveError }}</span>
        </div>
      </section>

      <!-- Project assignments -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="font-semibold">
            Project assignments
            <span class="text-neutral-400">({{ activeAssignments.length }} active, {{ pastAssignments.length }} past)</span>
          </h2>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            @click="showAssign = !showAssign"
          >
            Assign to project
          </button>
        </div>

        <div
          v-if="showAssign"
          class="mb-4 grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
        >
          <select
            v-model="newAssign.project_id"
            :class="inputCls"
          >
            <option value="">
              Select project…
            </option>
            <option
              v-for="p in projects"
              :key="p.id"
              :value="p.id"
            >
              {{ p.name }}
            </option>
          </select>
          <select
            v-model="newAssign.role"
            :class="inputCls"
          >
            <option value="">
              Role on project…
            </option>
            <option
              v-for="r in competencyRoles"
              :key="r.role"
              :value="r.role"
            >
              {{ r.role }}
            </option>
          </select>
          <label class="grid gap-1 text-xs text-neutral-500">Started
            <input
              v-model="newAssign.started_on"
              type="date"
              :class="inputCls"
            ></label>
          <p
            v-if="selectedRoleRequired.length"
            class="text-xs text-neutral-500 sm:col-span-2"
          >
            <span class="font-medium">{{ newAssign.role }}</span> requires:
            {{ selectedRoleRequired.map((r) => `${r.competency} (${r.level})`).join(", ") }}.
          </p>
          <div class="flex items-end">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="assignBusy || !newAssign.project_id"
              @click="saveAssignment"
            >
              {{ assignBusy ? "Assigning…" : "Assign" }}
            </button>
          </div>
        </div>

        <p
          v-if="activeAssignments.length"
          class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400"
        >
          Active
        </p>
        <ul class="grid gap-1">
          <li
            v-for="a in activeAssignments"
            :key="a.id"
            class="border-b border-neutral-50 py-2"
          >
            <div class="flex items-center justify-between">
              <span>
                <span class="font-medium text-neutral-900">{{ a.project_name || a.project_id }}</span>
                <span
                  v-if="a.role"
                  class="text-neutral-500"
                > · {{ a.role }}</span>
                <span class="text-sm text-neutral-400"> · Started {{ fmt(a.started_on) }}</span>
              </span>
              <div class="flex items-center gap-3">
                <span
                  v-if="a.required && a.required.length"
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="a.rosterable
                    ? 'bg-semantic-success/10 text-semantic-success'
                    : 'bg-semantic-danger/10 text-semantic-danger'"
                  :title="a.rosterable
                    ? 'Meets all three layers: Sentinel, internal pre-reqs, minimum-core'
                    : `${a.blockers?.length ?? 0} blocker(s) across Sentinel / pre-reqs / minimum-core`"
                >
                  {{ a.rosterable ? "Rosterable" : `Not rosterable · ${a.blockers?.length ?? 0}` }}
                </span>
                <button
                  type="button"
                  class="text-xs text-neutral-400 hover:text-semantic-danger"
                  @click="endAssign(a.id)"
                >
                  End
                </button>
              </div>
            </div>
            <ul
              v-if="a.blockers && a.blockers.length"
              class="mt-1 grid gap-0.5 pl-1"
            >
              <li
                v-for="(b, i) in a.blockers"
                :key="`b${i}`"
                class="flex items-center gap-2 text-sm text-neutral-600"
              >
                <span class="h-1.5 w-1.5 rounded-full bg-semantic-danger" />
                <span class="rounded bg-neutral-100 px-1.5 text-xs text-neutral-500">{{ LAYER_LABEL[b.layer] }}</span>
                {{ b.competency }}
                <span class="text-xs text-semantic-danger">{{ findingDetail(b) }}</span>
              </li>
            </ul>
            <ul
              v-if="a.warnings && a.warnings.length"
              class="mt-0.5 grid gap-0.5 pl-1"
            >
              <li
                v-for="(w, i) in a.warnings"
                :key="`w${i}`"
                class="flex items-center gap-2 text-sm text-neutral-500"
              >
                <span class="h-1.5 w-1.5 rounded-full bg-semantic-warning" />
                <span class="rounded bg-neutral-100 px-1.5 text-xs text-neutral-500">{{ LAYER_LABEL[w.layer] }}</span>
                {{ w.competency }}
                <span class="text-xs text-semantic-warning">{{ findingDetail(w) }}</span>
              </li>
            </ul>
          </li>
        </ul>

        <template v-if="pastAssignments.length">
          <p class="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Past
          </p>
          <ul class="grid gap-1">
            <li
              v-for="a in pastAssignments"
              :key="a.id"
              class="border-b border-neutral-50 py-2 text-sm text-neutral-500"
            >
              <span class="font-medium text-neutral-700">{{ a.project_name || a.project_id }}</span>
              · Started {{ fmt(a.started_on) }} · ended {{ fmt(a.ended_on) }}
            </li>
          </ul>
        </template>

        <p
          v-if="!activeAssignments.length && !pastAssignments.length"
          class="text-sm text-neutral-400"
        >
          Not assigned to any project yet.
        </p>
        <p
          v-if="activeAssignments.some((a) => a.required && a.required.length)"
          class="mt-3 text-xs text-neutral-400"
        >
          Role requirements are provisional, pending the client's competency
          matrix.
        </p>
      </section>

      <!-- Compliance dates -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-1 font-semibold">
          Compliance dates
        </h2>
        <p class="mb-4 text-xs text-neutral-400">
          Kept current automatically from verified cards (Sentinel / NCCA /
          medical) — a card never shortens a date already on file.
        </p>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div
            v-for="d in complianceDates"
            :key="d.label"
            class="rounded-lg border border-neutral-100 bg-neutral-50 p-3"
          >
            <p class="text-xs font-medium text-neutral-500">
              {{ d.label }}
            </p>
            <p class="mt-0.5 text-sm font-semibold text-neutral-900">
              {{ d.date ? fmt(d.date) : "—" }}
            </p>
            <span
              class="mt-1 inline-block rounded-full px-2 py-0.5 text-xs"
              :class="dateStatus(d.date, d.expiry).cls"
            >{{ dateStatus(d.date, d.expiry).label }}</span>
          </div>
        </div>
      </section>

      <!-- Competences -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-4 flex items-center justify-between gap-2">
          <h2 class="font-semibold">
            Competences ({{ data.competences.length }})
          </h2>
          <div class="flex shrink-0 items-center gap-2">
            <label
              class="cursor-pointer rounded-md border border-brand-primary/40 bg-brand-primary/5 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10"
              :class="{ 'pointer-events-none opacity-50': scanBusy }"
              title="Photograph a card (front, and back) — AI pre-fills the details to confirm"
            >
              {{ scanBusy ? "Reading…" : "📷 Scan a card" }}
              <input
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                :disabled="scanBusy"
                @change="scanCard"
              >
            </label>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              @click="showComp = !showComp"
            >
              Add competence
            </button>
          </div>
        </div>
        <p
          v-if="scanMsg"
          class="mb-3 rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-600"
        >
          {{ scanMsg }}
        </p>
        <table
          v-if="data.competences.length"
          class="w-full text-left text-sm"
        >
          <thead class="text-neutral-500">
            <tr>
              <th class="py-1 pr-4 font-medium">
                Category
              </th>
              <th class="py-1 pr-4 font-medium">
                Sub-category / body
              </th>
              <th class="py-1 pr-4 font-medium">
                Reference
              </th>
              <th class="py-1 pr-4 font-medium">
                Level
              </th>
              <th class="py-1 pr-4 font-medium">
                Expiry
              </th>
              <th class="py-1 pr-4 font-medium">
                Status
              </th>
              <th class="py-1" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in data.competences"
              :key="c.id"
              class="border-t border-neutral-100"
            >
              <td class="py-2 pr-4 font-medium text-neutral-900">
                {{ c.category }}
                <span
                  v-if="c.source === 'sentinel'"
                  class="ml-1.5 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-normal text-neutral-500"
                  :title="c.employer || c.office ? [c.employer, c.office].filter(Boolean).join(' · ') : 'Synced from Sentinel'"
                >Sentinel</span>
                <button
                  v-if="c.front_file_id"
                  type="button"
                  class="ml-1.5 text-xs font-normal text-brand-primary hover:underline"
                  title="View the card image"
                  @click="viewCard(c)"
                >
                  📎 card
                </button>
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ c.sub_category || c.alias || "—" }}
                <span
                  v-if="c.issuing_body"
                  class="block text-xs text-neutral-400"
                >{{ c.issuing_body }}</span>
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ c.reference || "—" }}
              </td>
              <td class="py-2 pr-4">
                <span
                  v-if="c.level"
                  class="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary"
                  :title="LEVEL_LABEL[c.level as string]"
                >{{ c.level }}</span>
                <span
                  v-else
                  class="text-neutral-300"
                >—</span>
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ fmt(c.expiry) }}
              </td>
              <td class="py-2 pr-4">
                <span
                  v-if="c.verification === 'pending'"
                  class="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700"
                  title="Recorded but not counted toward the rosterable gate until the office signs it off"
                >Pending sign-off</span>
                <span
                  v-else
                  class="rounded-full px-2 py-0.5 text-xs"
                  :class="status(c.expiry).cls"
                >{{ status(c.expiry).label }}</span>
              </td>
              <td class="py-2 text-right">
                <span
                  v-if="c.source === 'sentinel'"
                  class="text-xs text-neutral-300"
                  title="Managed by the Sentinel sync"
                >Synced</span>
                <template v-else>
                  <button
                    v-if="c.verification === 'pending'"
                    type="button"
                    class="mr-3 text-xs font-medium text-brand-primary hover:underline"
                    title="Confirm this card — counts toward the rosterable gate"
                    @click="verifyCard(c.id)"
                  >
                    Verify
                  </button>
                  <button
                    type="button"
                    class="text-xs text-neutral-400 hover:text-semantic-danger"
                    @click="dropCompetence(c.id)"
                  >
                    Remove
                  </button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No competences on file.
        </p>
        <div
          v-if="showComp"
          class="mt-4 grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
        >
          <label class="block sm:col-span-2">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Card type</span>
            <input
              v-model="newComp.category"
              list="card-type-suggestions"
              placeholder="e.g. CSCS, EUSR SHEA Power"
              :class="inputCls"
            >
            <datalist id="card-type-suggestions">
              <option
                v-for="t in CARD_TYPE_SUGGESTIONS"
                :key="t"
                :value="t"
              />
            </datalist>
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Sub-category (optional)</span>
            <input
              v-model="newComp.sub_category"
              placeholder="e.g. &quot;Twin Lanyard&quot;"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Card / certificate number</span>
            <input
              v-model="newComp.reference"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Issuing body</span>
            <input
              v-model="newComp.issuing_body"
              placeholder="e.g. CITB, EUSR"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Minimum-Core level</span>
            <select
              v-model="newComp.level"
              :class="inputCls"
            >
              <option value="">
                Not assessed
              </option>
              <option
                v-for="lvl in ['A', 'W', 'P', 'S']"
                :key="lvl"
                :value="lvl"
              >
                {{ lvl }} — {{ LEVEL_LABEL[lvl] }}
              </option>
            </select>
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Issued date</span>
            <input
              v-model="newComp.issued"
              type="date"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Expiry date</span>
            <input
              v-model="newComp.expiry"
              type="date"
              :class="inputCls"
            >
          </label>
          <p
            v-if="newComp.front_file_id"
            class="text-xs text-emerald-600 sm:col-span-2"
          >
            📎 Card image attached as evidence.
          </p>
          <label class="flex items-start gap-2 sm:col-span-2">
            <input
              v-model="newComp.pending"
              type="checkbox"
              class="mt-0.5"
            >
            <span class="text-xs text-neutral-500">
              Flag as <strong>pending verification</strong> — the card is recorded
              but doesn't count toward the rosterable gate until the office signs
              it off. Leave unticked if you're confirming it now.
            </span>
          </label>
          <div class="sm:col-span-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="compBusy || !newComp.category.trim()"
              @click="saveCompetence"
            >
              {{ compBusy ? "Adding…" : "Add competence" }}
            </button>
          </div>
        </div>
      </section>

      <!-- Medical records -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-1 flex items-center justify-between">
          <h2 class="font-semibold">
            Medical records ({{ data.medicals.length }})
          </h2>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            @click="showMed = !showMed"
          >
            Add medical
          </button>
        </div>
        <p class="mb-4 text-xs text-neutral-400">
          Sensitive — exclude from public share links unless the recipient
          explicitly needs medical evidence.
        </p>
        <table
          v-if="data.medicals.length"
          class="w-full text-left text-sm"
        >
          <thead class="text-neutral-500">
            <tr>
              <th class="py-1 pr-4 font-medium">
                Level
              </th>
              <th class="py-1 pr-4 font-medium">
                Provider
              </th>
              <th class="py-1 pr-4 font-medium">
                Examined
              </th>
              <th class="py-1 pr-4 font-medium">
                Expires
              </th>
              <th class="py-1 pr-4 font-medium">
                Result
              </th>
              <th class="py-1" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in data.medicals"
              :key="m.id"
              class="border-t border-neutral-100"
            >
              <td class="py-2 pr-4 font-medium text-neutral-900">
                {{ m.level }}
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ m.provider || "—" }}
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ fmt(m.examined) }}
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ fmt(m.expires) }}
              </td>
              <td class="py-2 pr-4 text-neutral-900">
                {{ m.result || "—" }}
              </td>
              <td class="py-2 text-right">
                <button
                  type="button"
                  class="text-xs text-neutral-400 hover:text-semantic-danger"
                  @click="dropMedical(m.id)"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No medical records on file.
        </p>
        <div
          v-if="showMed"
          class="mt-4 grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
        >
          <input
            v-model="newMed.level"
            placeholder="Level (e.g. Safety critical)"
            :class="inputCls"
          >
          <input
            v-model="newMed.provider"
            placeholder="Provider (optional)"
            :class="inputCls"
          >
          <label class="grid gap-1 text-xs text-neutral-500">Examined
            <input
              v-model="newMed.examined"
              type="date"
              :class="inputCls"
            ></label>
          <label class="grid gap-1 text-xs text-neutral-500">Expires
            <input
              v-model="newMed.expires"
              type="date"
              :class="inputCls"
            ></label>
          <input
            v-model="newMed.result"
            placeholder="Result (e.g. Fit)"
            :class="inputCls"
          >
          <div class="sm:col-span-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="medBusy || !newMed.level.trim()"
              @click="saveMedical"
            >
              {{ medBusy ? "Adding…" : "Add" }}
            </button>
          </div>
        </div>
      </section>

      <!-- D&A screens -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-1 flex items-center justify-between">
          <h2 class="font-semibold">
            D&amp;A screens ({{ data.dna_screens.length }})
          </h2>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            @click="showDna = !showDna"
          >
            Add D&amp;A screen
          </button>
        </div>
        <p class="mb-4 text-xs text-neutral-400">
          Sensitive — exclude from public share links unless the recipient
          explicitly needs the result (e.g. Network Rail / utility client
          compliance teams).
        </p>
        <table
          v-if="data.dna_screens.length"
          class="w-full text-left text-sm"
        >
          <thead class="text-neutral-500">
            <tr>
              <th class="py-1 pr-4 font-medium">
                Type
              </th>
              <th class="py-1 pr-4 font-medium">
                Provider
              </th>
              <th class="py-1 pr-4 font-medium">
                Date
              </th>
              <th class="py-1 pr-4 font-medium">
                Result
              </th>
              <th class="py-1" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="d in data.dna_screens"
              :key="d.id"
              class="border-t border-neutral-100"
            >
              <td class="py-2 pr-4 font-medium text-neutral-900">
                {{ d.screen_type }}
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ d.provider || "—" }}
              </td>
              <td class="py-2 pr-4 text-neutral-500">
                {{ fmt(d.date) }}
              </td>
              <td class="py-2 pr-4 text-neutral-900">
                {{ d.result || "—" }}
              </td>
              <td class="py-2 text-right">
                <button
                  type="button"
                  class="text-xs text-neutral-400 hover:text-semantic-danger"
                  @click="dropDna(d.id)"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No D&amp;A screens on file. Add a pre-employment, random, or for-cause
          screen to start tracking.
        </p>
        <div
          v-if="showDna"
          class="mt-4 grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
        >
          <input
            v-model="newDna.screen_type"
            placeholder="Type (e.g. Pre-employment)"
            :class="inputCls"
          >
          <input
            v-model="newDna.provider"
            placeholder="Provider (optional)"
            :class="inputCls"
          >
          <label class="grid gap-1 text-xs text-neutral-500">Date
            <input
              v-model="newDna.date"
              type="date"
              :class="inputCls"
            ></label>
          <input
            v-model="newDna.result"
            placeholder="Result (e.g. Negative)"
            :class="inputCls"
          >
          <div class="sm:col-span-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="dnaBusy || !newDna.screen_type.trim()"
              @click="saveDna"
            >
              {{ dnaBusy ? "Adding…" : "Add" }}
            </button>
          </div>
        </div>
      </section>

      <!-- Documents -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="font-semibold">
            Documents ({{ data.documents.length }})
          </h2>
          <label
            class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          >
            {{ docBusy ? "Uploading…" : "Upload document" }}
            <input
              type="file"
              class="hidden"
              :disabled="docBusy"
              @change="onDocument"
            >
          </label>
        </div>
        <ul
          v-if="data.documents.length"
          class="grid gap-1"
        >
          <li
            v-for="doc in data.documents"
            :key="doc.id"
            class="flex items-center justify-between border-b border-neutral-50 py-2 text-sm"
          >
            <button
              type="button"
              class="text-brand-primary hover:underline"
              @click="openDocument(doc.file_id)"
            >
              {{ doc.name }}
            </button>
            <button
              type="button"
              class="text-xs text-neutral-400 hover:text-semantic-danger"
              @click="dropDocument(doc.id)"
            >
              Remove
            </button>
          </li>
        </ul>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No documents on file. Upload policies, certificates, right-to-work
          checks, or any other supporting paperwork.
        </p>
      </section>

      <!-- Addresses (home / hotel / alternate, for travel time) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-1 flex items-center justify-between">
          <h2 class="font-semibold">
            Addresses ({{ data.addresses.length }})
          </h2>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            @click="showAddr ? resetAddr() : (showAddr = true)"
          >
            {{ showAddr ? "Close" : "Add address" }}
          </button>
        </div>
        <p class="mb-4 text-xs text-neutral-400">
          Home, hotel/lodging and alternate addresses — shift travel time is
          computed from the worker's primary home (or assigned accommodation).
        </p>
        <ul
          v-if="data.addresses.length"
          class="mb-4 divide-y divide-neutral-100"
        >
          <li
            v-for="a in data.addresses"
            :key="a.id"
            class="flex items-center justify-between gap-3 py-2 text-sm"
          >
            <div>
              <span class="font-medium capitalize">{{ a.type }}</span>
              <span
                v-if="a.is_primary"
                class="ml-1.5 rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs text-brand-primary"
              >primary</span>
              <span
                v-if="a.latitude == null"
                class="ml-1.5 rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
                title="Not geocoded — travel time can't be computed"
              >no coords</span>
              <span class="ml-2 text-neutral-500">
                {{ [a.label, a.line1, a.city, a.postcode].filter(Boolean).join(", ") }}
              </span>
            </div>
            <div class="flex shrink-0 gap-3">
              <button
                type="button"
                class="text-xs text-brand-primary hover:underline"
                @click="editAddr(a)"
              >
                Edit
              </button>
              <button
                type="button"
                class="text-xs text-neutral-400 hover:text-semantic-danger"
                @click="dropAddr(a.id)"
              >
                Remove
              </button>
            </div>
          </li>
        </ul>
        <p
          v-else
          class="mb-4 text-sm text-neutral-400"
        >
          No addresses on file.
        </p>

        <div
          v-if="showAddr"
          class="grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
        >
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Type</span>
            <select
              v-model="newAddr.type"
              :class="inputCls"
            >
              <option
                v-for="t in ADDRESS_TYPES"
                :key="t.value"
                :value="t.value"
              >
                {{ t.label }}
              </option>
            </select>
          </label>
          <label
            v-if="newAddr.type === 'hotel' && activeAccommodations.length"
            class="block"
          >
            <span class="mb-1 block text-xs font-medium text-neutral-500">From catalogue</span>
            <select
              :class="inputCls"
              @change="pickAccommodation(($event.target as HTMLSelectElement).value)"
            >
              <option value="">
                Pick a saved hotel…
              </option>
              <option
                v-for="a in activeAccommodations"
                :key="a.id"
                :value="a.id"
              >
                {{ a.name }} ({{ a.postcode }})
              </option>
            </select>
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Label (e.g. hotel name)</span>
            <input
              v-model="newAddr.label"
              :class="inputCls"
            >
          </label>
          <label class="block sm:col-span-2">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Address line 1</span>
            <input
              v-model="newAddr.line1"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">City</span>
            <input
              v-model="newAddr.city"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium text-neutral-500">Postcode</span>
            <div class="flex gap-2">
              <input
                v-model="newAddr.postcode"
                placeholder="e.g. G1 1AA"
                :class="[inputCls, 'flex-1']"
              >
              <button
                type="button"
                class="shrink-0 rounded-md border border-neutral-300 px-3 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
                :disabled="geoBusy || !newAddr.postcode.trim()"
                @click="geocodeAddr"
              >
                {{ geoBusy ? "…" : "Lookup" }}
              </button>
            </div>
          </label>
          <label class="flex items-center gap-2 sm:col-span-2">
            <input
              v-model="newAddr.is_primary"
              type="checkbox"
            >
            <span class="text-xs text-neutral-500">Primary {{ newAddr.type }} address (used for travel)</span>
          </label>
          <p
            v-if="geoMsg"
            class="text-xs text-neutral-500 sm:col-span-2"
          >
            {{ geoMsg }}
          </p>
          <div class="sm:col-span-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="addrBusy || !newAddr.postcode.trim()"
              @click="saveAddr"
            >
              {{ addrBusy ? "Saving…" : "Save address" }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </PageLayout>
</template>
